import { getAwsCredentials } from 'src/authentication/cognito';
import { callSNOWDataService } from './snowDataServiceWrapper';
import { getSessionID, logger } from 'src/utils/logger';
import {
	APOLLO_PERMISSION_ACTIONS,
	PERMISSIONS_TEAM_URL,
	SNOW_PERMISSIONS_TO_ACTOR_MAP,
} from 'src/components/ResilienceWorkflow/ResilienceConstants';
import {
	COUNTRIES,
	SOMETHING_WENT_WRONG,
	USER_IS_OFFLINE,
	SNOW_CREATE_SIM_ISSUES_LINK,
} from 'src/constants/CommonConstants';
import AWS from 'aws-sdk';
import { v4 as uuidv4 } from 'uuid';
import { SNOW_NXT_WORKFLOW_ENDPOINT } from 'src/config';

export function getTaskStatusId(TaskStatus) {
	return 'FAILED' === TaskStatus
		? 5
		: 'COMPLETED' === TaskStatus
		? 1
		: 'RUNNING' === TaskStatus
		? 2
		: 'OVERRIDEN' === TaskStatus
		? 3
		: 4;
}

export function getMatchesCountText(count) {
	return count === 1 ? '1 match' : `${count} matches`;
}

export function sortedTaskStatus(a, b) {
	if (getTaskStatusId(a) < getTaskStatusId(b)) {
		return 1;
	}

	if (getTaskStatusId(a) > getTaskStatusId(b)) {
		return -1;
	}

	return 0;
}

export const validateForm = (tabName, fields, values) => {
	const errors = {};

	logger.info(`Validating the tab - ${tabName} ${fields} ${values}`);
	Object.keys(fields).forEach((key) => {
		if (fields[key].mandatory && !values[key]) {
			errors[
				`${tabName}-${key}`
			] = ` ${tabName} - ${fields[key].name} can't be empty`;
		} else if (
			fields[key].regEx &&
			values[key] &&
			!fields[key].regEx.test(values[key])
		) {
			errors[
				`${tabName}-${key}`
			] = ` ${tabName} - ${fields[key].name} didn't follow the constraint "${fields[key].hintText}"`;
		}
	});
	logger.info(`Errors while validating the ${tabName} tab - ${errors}`);

	return errors;
};

export const validatePermissions = async (bindleName) => {
	let errors = {};

	logger.info(`Validating the permissions for SNOW on bindle - ${bindleName}`);
	if (!bindleName) {
		errors['Bindle'] = 'Apollo Environment Name is blank';

		return errors;
	}

	await getAwsCredentials();
	const credentials = {
		accessKey: AWS.config.credentials.accessKeyId,
		secretKey: AWS.config.credentials.secretAccessKey,
		sessionToken: AWS.config.credentials.sessionToken,
	};

	const requests = [];

	APOLLO_PERMISSION_ACTIONS.map((action) => {
		Object.keys(SNOW_PERMISSIONS_TO_ACTOR_MAP).map((actor) => {
			requests.push(
				callAuthorizeApi(
					credentials,
					bindleName,
					actor,
					SNOW_PERMISSIONS_TO_ACTOR_MAP[actor],
					action
				)
			);
		});
	});

	await Promise.all(requests).then((results) => {
		results.forEach((result) => {
			errors = { ...errors, ...result };
		});
	});

	return errors;
};

export const callAuthorizeApi = async (
	credentials,
	bindleName,
	actor,
	type,
	action
) => {
	const errors = {};

	logger.info(
		`Validating the permission [ Bindle : Actor : Action] -> [ ${bindleName} : ${actor} : ${action}]`
	);
	try {
		const resource = {
			type: 'environment',
			namespace: 'apollo',
			action,
			ids: [bindleName],
		};

		const apiResponse = await callSNOWDataService({
			path: '/authorize',
			method: 'POST',
			body: {
				user: actor,
				resource,
				requestId: uuidv4(),
				actorType: type,
			},
			credentials,
		});

		if (apiResponse.authorizedResource.ids.includes(bindleName)) {
			logger.info(
				`Bindle ${bindleName} has the permission [ Actor : Action] -> [${actor} : ${action}]`
			);
		} else {
			logger.error(
				`Bindle ${bindleName} doesn't have permissions [ Actor : Action] -> [${actor} : ${action}]`
			);
			errors[
				`Bindle - ${actor} - ${action}`
			] = `Bindle ${bindleName} doesn't have permissions [ Actor : Action] -> [${actor} : ${action}]. Provide 1-click bindles permissions in Pre-requisites page.`;
		}
	} catch (e) {
		logger.error(e);
		errors[
			`Bindle - ${actor} - ${action}`
		] = `Error occured while validating the permissions. [ Bindle : Actor : Action] -> [ ${bindleName} : ${actor} : ${action}] -     ${e.message()}`;
	}

	return errors;
};

export const startWorkflow = async (workflowType, entityName) => {
	try {
		await getAwsCredentials();
		const lambdaClient = new AWS.Lambda({
			apiVersion: '2015-03-31',
			region: 'us-east-1',
		});
		const params = {
			FunctionName: 'startNewCycleForEntity',
			Payload: JSON.stringify({
				workflowType,
				entityName,
			}),
		};

		logger.info(
			`Starting new cycle for entity ${entityName} and for ${workflowType}`
		);
		const lambdaResponse = await lambdaClient.invoke(params).promise();

		logger.info(
			`Got response from startNewCycleForEntity lambda: ${JSON.stringify(
				lambdaResponse
			)}`
		);
		if (
			lambdaResponse.StatusCode === 200 &&
			lambdaResponse.Payload &&
			JSON.parse(lambdaResponse.Payload).success
		) {
			logger.info(
				`Cycle successfully started for entity ${entityName} and for ${workflowType}`
			);

			return {
				success: JSON.parse(lambdaResponse.Payload).success,
			};
		} else {
			logger.error(
				`Cycle couldn't be started for Entity ${entityName} and for ${workflowType}`
			);

			return {
				success: false,
				errorMessage: lambdaResponse.errorMessage,
			};
		}
	} catch (e) {
		logger.error(
			`Cycle couldn't be started for Entity ${entityName} for ${workflowType}. Error - ${e.message}`
		);
		throw e;
	}
};

export const getEntities = async (request, credentials = null) => {
	try {
		const apiResponse = await callSNOWDataService({
			path: '/entity',
			method: 'GET',
			queryParams: request,
			credentials,
		});

		logger.info(
			`Response from read entity - ${JSON.stringify(apiResponse.entities)}`
		);

		return apiResponse;
	} catch (err) {
		logger.error(
			`Exception occured while querying SDS for request - ${request} - ${err}`
		);
		throw err;
	}
};

export const getWorkflowDefinitions = async (request, credentials = null) => {
	try {
		const apiResponse = await callSNOWDataService({
			path: '/workflows',
			method: 'GET',
			queryParams: request,
			url: SNOW_NXT_WORKFLOW_ENDPOINT,
			credentials,
		});

		logger.info(
			`Response from ListWorkflowDefinitions - ${JSON.stringify(apiResponse)}`
		);

		return apiResponse;
	} catch (err) {
		logger.error(
			`Exception occured while calling ListWorkflowDefinitions for request - ${request} - ${err}`
		);
		throw err;
	}
};

export const isAwsAccountExist = async (accountName) => {
	logger.info(`Validating ${accountName} Existance`);

	await getAwsCredentials();
	const lambdaClient = new AWS.Lambda({
		apiVersion: '2015-03-31',
		region: 'us-east-1',
	});
	const params = {
		FunctionName: 'SNOWFindAwsAccount',
		Payload: JSON.stringify({
			accountName,
		}),
	};
	const lambdaResponse = await lambdaClient.invoke(params).promise();

	logger.info(
		`Got response from SNOWFindAwsAccount lambda: ${JSON.stringify(
			lambdaResponse
		)}`
	);
	if (
		lambdaResponse.StatusCode === 200 &&
		lambdaResponse.Payload &&
		JSON.parse(lambdaResponse.Payload).status !== 'active'
	) {
		logger.info(`${accountName} Aws account doesn't exist`);

		return false;
	} else {
		logger.info(`${accountName} - Aws account does exist`);

		return true;
	}
};

export const getTeamsUrl = (team) => `${PERMISSIONS_TEAM_URL}/${team}`;

//Two months date from now. For providing permissions to Service team bindle temporarily.
export const getExpirationDate = () => {
	const currentDate = new Date();

	return new Date(currentDate.setMonth(currentDate.getMonth() + 2));
};

export function convertCamelCaseToSentence(camelCase) {
	if (camelCase) {
		const result = camelCase.replace(/([A-Z])/g, (match) => {
			return ' ' + match;
		});

		return result[0].toUpperCase() + result.substring(1).toLowerCase();
	}

	return '';
}

export function getApiErrorMessage(error) {
	if (error?.message) {
		return error.message;
	}

	if (window.navigator.onLine) {
		return SOMETHING_WENT_WRONG;
	}

	return USER_IS_OFFLINE;
}

export function isString(value) {
	if (typeof value === 'string' || value instanceof String) {
		return true;
	}

	return false;
}

export function requiredText(value) {
	if (!(isString(value) && value)) {
		return 'You must provide some text.';
	}
}

// when cloning regex it doesn’t preserve RegExp lastIndex property
export function deepCopy(value) {
	return structuredClone(value);
}

export const convertStringToArray = (stringValue, splitter = '\n') => {
	return stringValue.split(splitter);
};

export async function isUserPresentInBindle(
	bindleName,
	userName,
	resourceType = 'SoftwareApp',
	action = 'Can Manage',
	nameSpace = 'Bindle',
	credentials
) {
	logger.info(
		`Validating the permission [ bindleName : Action : userName : resourceType] -> [ ${bindleName} : ${action} : ${userName} : ${resourceType}]`
	);
	try {
		const resource = {
			type: resourceType,
			namespace: nameSpace,
			action,
			ids: [bindleName],
		};

		const apiResponse = await callSNOWDataService({
			path: '/authorize',
			method: 'POST',
			body: {
				user: userName,
				resource,
				requestId: uuidv4(),
			},
			credentials,
		});

		if (apiResponse.authorizedResource.ids.includes(bindleName)) {
			logger.info(
				`Bindle ${bindleName} has the permission [ UserName : Action] -> [${userName} : ${action}]`
			);
		} else {
			logger.error(
				`Bindle ${bindleName} doesn't have permissions [ UserName : Action] -> [${userName} : ${action}]`
			);
			throw new Error(
				`Bindle ${bindleName} doesn't have permissions [ UserName : Action] -> [${userName} : ${action}]. Provide 1-click bindles permissions in Pre-requisites page.`
			);
		}
	} catch (e) {
		logger.error(e);
		throw new Error(
			`Error occured while validating the permissions. [ Bindle : UserName : Action] -> [ ${bindleName} : ${userName} : ${action}] -    ${e.message()} ]`
		);
	}
}

export function validateNodeNameWithRegex(value) {
	const regexLiteral = /^[A-Z\d]{4}$/;
	let errorText = '';

	if (!regexLiteral.test(value)) {
		errorText = 'RegEx Validation Failed, refer example - BFI1, OAK2';
	}

	return errorText;
}

export function validateNumberWithRegex(value) {
	const regexLiteral = /^[0-9]/;
	let errorText = '';

	if (!value) return;
	if (!regexLiteral.test(value)) {
		errorText = 'RegEx Validation Failed, value should be a number!';
	}

	return errorText;
}

export const getValidationTaskForAddressValidation = (
	nodeName,
	attributeData
) => {
	return [
		{
			functionName: 'ValidateEntityParametersProxy',
			validationTaskType: 'validateAddressTask',
			entityParameters: {
				name: nodeName,
				address_line_1: attributeData.physical_address_line1,
				country: attributeData.physical_country,
				city: attributeData.physical_address_city,
				postal_code: attributeData.physical_address_pincode,
				state: attributeData.physical_address_state,
				address_line_2: attributeData.physical_address_line2 ?? '',
				address_line_3: attributeData.physical_address_line3 ?? '',
			},
			entityPresent: true,
		},
	];
};

export const getCountries = (region) => {
	return COUNTRIES[region] || [];
};

export const getTimeFromEpoch = (epochTime) => {
	const d = new Date(0); // The 0 there is the key, which sets the date to the epoch

	d.setUTCSeconds(epochTime);

	return d.toLocaleDateString();
};

export const getDateAndTimeFromEpoch = (epochTime) => {
	const d = new Date(0);

	d.setUTCSeconds(epochTime);

	return d.toUTCString();
};

export const getLocaleDateFromEpoch = (epochTime) => {
	const d = new Date(0);

	d.setUTCSeconds(Number(epochTime));

	return d.toDateString();
};

export const generateTitleFromDBKey = (dbKey) => {
	return dbKey.replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase();
};

/**
 * Converts literal escape sequences (e.g. the two characters "\" + "n") that
 * arrive in API strings into their real whitespace characters so they render
 * correctly (as line breaks/tabs) once passed to a markdown renderer.
 * If the string already contains real newlines, they are left untouched.
 */
export const normalizeEscapedWhitespace = (value) => {
	if (typeof value !== 'string') {
		return value;
	}

	return value
		.replace(/\\r\\n/g, '\n')
		.replace(/\\n/g, '\n')
		.replace(/\\r/g, '\n')
		.replace(/\\t/g, '\t');
};

export const formatDate = (date) => {
	return new Date(date).toUTCString();
};

export const getRiverLinkWithSessionId = () => {
	return `${SNOW_CREATE_SIM_ISSUES_LINK}&sessionId=${getSessionID()}`;
};

/**
 * convertAddressToASCII
 *
 * • Removes every accent/diacritic (NFKD = includes º → o, æ → ae, etc.)
 * • Allows  A–Z  a–z  0–9  space  #  ,  .  /  -
 * • Turns anything else into a single space
 * • Collapses multiple spaces and trims
 */
export const convertAddressToASCII = (input = '') =>
	input
		.normalize('NFKD')
		.replace(/\p{M}/gu, '')
		.replace(/[^A-Za-z0-9 #:,.\-/]/g, ' ')
		.replace(/\s{2,}/g, ' ')
		.trim();

export const downloadCSV = (items, columns, filename) => {
	if (!items.length) return;

	const visibleColumns = columns.filter(col => col.id !== 'actions');
	const headers = visibleColumns.map(col => col.header);

	const rows = items.map(item =>
		visibleColumns.map(col => {
			const value = item[col.id];
			if (value === null || value === undefined) return '';
			const str = String(value).replace(/"/g, '""');
			return `"${str}"`;
		})
	);

	const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
	const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = `${filename}.csv`;
	link.click();
	URL.revokeObjectURL(url);
};

export const getFullDateFromEpoch = (date) => {
	const options = {
		weekday: 'long',
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	};

	return date ? new Date(date).toLocaleDateString(undefined, options) : 'N/A';
};

/**
 * Recursively parses nested JSON strings within an object or array.
 * Useful for handling API responses where JSON is stringified multiple times.
 *
 * @param {any} value - The value to parse (can be string, object, array, or primitive)
 * @returns {any} - The parsed value with all nested JSON strings converted to objects
 * 
 * @example
 * // Single level parsing
 * deepParseJson('{"key":"value"}') // Returns: { key: "value" }
 * 
 * @example
 * // Nested JSON parsing
 * deepParseJson({
 *   payload: '{"nested":"{\\"deep\\":\\"value\\"}"}'
 * })
 * // Returns: { payload: { nested: { deep: "value" } } }
 * 
 * @example
 * // Array with nested JSON
 * deepParseJson(['{"id":1}', '{"id":2}'])
 * // Returns: [{ id: 1 }, { id: 2 }]
 */
export const deepParseJson = (value) => {
	if (typeof value !== 'string') {
		if (Array.isArray(value)) {
			return value.map(item => deepParseJson(item));
		}
		
		if (value !== null && typeof value === 'object') {
			return Object.entries(value).reduce((acc, [key, val]) => ({
				...acc,
				[key]: deepParseJson(val),
			}), {});
		}
		
		return value;
	}

	try {
		const parsed = JSON.parse(value);

		return deepParseJson(parsed);
	} catch (error) {
		return value;
	}
};

/**
 * Parses a stringified JSON output, handling nested JSON strings.
 * 
 * @param {string} output - The stringified JSON output
 * @returns {string | object} - The parsed object or original string if parsing fails
 */
export const parseTaskOutput = (output) => {
	try {
		const parsed = JSON.parse(output);

		return deepParseJson(parsed);
	} catch (error) {
		return output;
	}
};

let _isMac = null;
export function isMacPlatform() {
	if (_isMac === null) {
		_isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
	}
	return _isMac;
}

const SHORTCUTS = {
	undo: { mac: 'Cmd+Z', other: 'Ctrl+Z' },
	redo: { mac: 'Cmd+Shift+Z', other: 'Ctrl+Y' },
};

export function getUndoShortcut() {
	return isMacPlatform() ? SHORTCUTS.undo.mac : SHORTCUTS.undo.other;
}

export function getRedoShortcut() {
	return isMacPlatform() ? SHORTCUTS.redo.mac : SHORTCUTS.redo.other;
}

export function isUndoShortcut(event) {
	const isMac = isMacPlatform();
	const ctrlKey = isMac ? event.metaKey : event.ctrlKey;
	return ctrlKey && event.key === 'z' && !event.shiftKey;
}

export function isRedoShortcut(event) {
	const isMac = isMacPlatform();
	const ctrlKey = isMac ? event.metaKey : event.ctrlKey;
	return (
		(ctrlKey && event.key === 'y') ||
		(ctrlKey && event.shiftKey && event.key === 'z')
	);
}

export const toCommaSeparated = (value) => {
	if (Array.isArray(value)) return value.join(', ');
	if (typeof value === 'string' && value.length > 0) return value;
	return '';
};